/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import { CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import BlogsTableData from "Views/Blogs/data/BlogsTableData";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import { Link } from "react-router-dom";
import api from "utils/api";
import { toast } from "react-toastify";

function Tables() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch all blogs
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const response = await api.get("/blogs");
      if (response.data.success) {
        setBlogs(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast.error(error.response?.data?.message || "Failed to fetch blogs");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Delete blog
  const handleDelete = async () => {
    if (!selectedBlog?._id) return;

    setLoading(true);
    try {
      const response = await api.delete(`/blogs/${selectedBlog._id}`);
      if (response.data.success) {
        toast.success(response.data.message || "Blog deleted successfully!");
        setDeleteDialog(false);
        setSelectedBlog(null);
        fetchBlogs();
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast.error(error.response?.data?.message || "Failed to delete blog");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (blog) => {
    setSelectedBlog(blog);
    setDeleteDialog(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialog(false);
    setSelectedBlog(null);
  };

  // Search filter
  const filteredBlogs = blogs.filter((blog) => {
    const search = searchTerm.toLowerCase();
    return (
      blog.title?.toLowerCase().includes(search) ||
      blog.category?.toLowerCase().includes(search) ||
      blog.short_desc?.toLowerCase().includes(search)
    );
  });

  const { columns, rows } = BlogsTableData(filteredBlogs, handleDeleteClick, fetchBlogs);

  // Fetch blogs on component mount
  useEffect(() => {
    fetchBlogs();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={2}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={3}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Blogs ({filteredBlogs.length})
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="Search by title, category"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ margin: "10px", minWidth: "250px" }}
                  />
                  <ArgonButton
                    size="small"
                    component={Link}
                    to="/blogs/addblog"
                    variant="contained"
                    color="primary"
                  >
                    Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            {loading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <CircularProgress />
              </ArgonBox>
            ) : filteredBlogs.length === 0 ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" py={5}>
                <ArgonTypography variant="body2" color="text">
                  {searchTerm
                    ? "No blogs found matching your search"
                    : "No blogs available. Add one to get started!"}
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <Table columns={columns} rows={rows} />
              </ArgonBox>
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDelete}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this blog?"
        description="This action is irreversible. Please be certain."
        cancelText="Cancel"
        confirmText="Delete"
        loading={loading}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
